import { WEAPONS, type Projectile, type WeaponId } from '@cuboom/shared';
import type { Particles } from '../fx/particles.js';

/**
 * Per-projectile flight colors — shared by the live renderer (explosion
 * flashes) and the art-showcase page.
 */
export const PROJ_COLORS: Record<string, string> = {
  pomegranate: '#c62f4a',
  grapes: '#7a4fb5',
  grapelet: '#8a5fd0',
  watermelon: '#3fa34d',
  coconut: '#8a5a2b',
  bamboo: '#6fae4e',
  beans: '#7cb86a',
  hen: '#f2e6c8',
  beehive: '#e0a53b',
  pigeon: '#c9ccd4',
  carrot: '#f08a2a',
  lemon: '#e8e04a',
  chili: '#d94f23',
  egg: '#f7f4ec',
};

export interface ProjectileDrawOpts {
  /** Clock seconds — idle sway, flap cycles, flame flicker. */
  nowS: number;
  /** Particle emitter for sparks/smoke; the showcase passes a no-op. */
  fx: Pick<Particles, 'burst'>;
}

/**
 * One projectile's flight art — shape by what the shot *does*, not just its
 * color: a thorn reads flat, a hen reads round, the hive reads striped.
 * Weapon-specific blocks return early; anything unrecognized falls through
 * to the generic ball. Extracted from main.ts verbatim so the art-showcase
 * page can draw the exact same art for review.
 */
export function drawProjectile(
  ctx: CanvasRenderingContext2D,
  p: Projectile,
  opts: ProjectileDrawOpts,
): void {
  const { nowS, fx } = opts;
    const def = WEAPONS[p.weaponId];
    const color = PROJ_COLORS[p.weaponId] ?? '#e8c24a';
    const ang = Math.atan2(p.vy, p.vx);
    if (p.weaponId === 'egg') {
      // Small white egg — a little drop of breakfast.
      ctx.fillStyle = '#f7f4ec';
      ctx.beginPath();
      ctx.ellipse(p.x, p.y, 5, 6.4, ang + Math.PI / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(60,50,40,.4)';
      ctx.lineWidth = 1.3;
      ctx.stroke();
      return;
    }
    if (p.weaponId === 'bamboo') {
      // Flat spike: a short needle along the flight path.
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(9, 0);
      ctx.lineTo(-7, 3);
      ctx.lineTo(-7, -3);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'carrot') {
      // Mortar carrot: orange nose forward, leafy top trailing.
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang);
      ctx.fillStyle = '#f08a2a';
      ctx.beginPath();
      ctx.moveTo(9, 0);
      ctx.quadraticCurveTo(2, 4, -5, 3.2);
      ctx.lineTo(-5, -3.2);
      ctx.quadraticCurveTo(2, -4, 9, 0);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(179,95,20,.7)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(3, -2);
      ctx.lineTo(4, 2);
      ctx.stroke();
      ctx.fillStyle = '#3fa34d';
      for (const ly of [-2.6, 0, 2.6]) {
        ctx.beginPath();
        ctx.ellipse(-7, ly * 0.8, 3, 1.6, ly * 0.35, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      return;
    }
    if (p.weaponId === 'hen') {
      // Frantic egg-layer mid-throw — and the same bird that rides the hand
      // (held gear replays this art): plump gradient breast with speckles, a
      // wing that really swings on the beat, fanned tail, three-bump comb,
      // wattle, catchlight eye and scrabbling feet. Authored facing +x,
      // mirrored for travel direction; the bob keeps the old cadence.
      const r = 11 + Math.sin(nowS * 18) * 1.5; // frantic flap bob
      const flap = Math.sin(nowS * 24); // -1..1 wingbeat
      const dir = Math.sign(p.vx || 1);
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.scale(dir, 1);

      // Tail: three feathers fanned off the back.
      for (const a of [-0.5, -0.08, 0.34]) {
        ctx.save();
        ctx.translate(-r + 3, 1);
        ctx.rotate(a);
        ctx.fillStyle = '#f1e9d6';
        ctx.strokeStyle = 'rgba(140,120,88,.75)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(-7, 0, 8, 3.1, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }

      // Body: cream breast lit from above, duskier belly rim.
      const bg = ctx.createRadialGradient(-2.5, -4, 1.5, 0, 0, r + 3);
      bg.addColorStop(0, '#fdfaf3');
      bg.addColorStop(0.6, '#f6f1e3');
      bg.addColorStop(1, '#e4d9bf');
      ctx.fillStyle = bg;
      ctx.beginPath();
      ctx.ellipse(0, 0, r, r * 0.8, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(130,110,80,.55)';
      ctx.lineWidth = 1.2;
      ctx.stroke();
      // Faint breast speckles so the cream doesn't read flat at zoom.
      ctx.fillStyle = 'rgba(205,185,145,.55)';
      for (const [dx, dy] of [
        [3, 4.5],
        [-1, 6],
        [6, 2],
      ]) {
        ctx.beginPath();
        ctx.ellipse(dx, dy, 1.4, 1, 0.3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Legs: stubby orange legs scrabbling with the flap, three toes each.
      ctx.strokeStyle = '#e08a2b';
      ctx.lineWidth = 1.7;
      ctx.lineCap = 'round';
      for (const [lx, ph] of [
        [-2.5, 0],
        [1.8, 2.2],
      ] as const) {
        const sw = Math.sin(nowS * 18 + ph) * 2.4;
        const footX = lx + sw;
        const footY = r * 0.62 + 4.5 + Math.abs(sw) * 0.3;
        ctx.beginPath();
        ctx.moveTo(lx, r * 0.4);
        ctx.quadraticCurveTo(lx + sw * 0.4, r * 0.62, footX, footY);
        ctx.stroke();
        for (const tx of [-2.2, 0.3, 2.4]) {
          ctx.beginPath();
          ctx.moveTo(footX, footY);
          ctx.lineTo(footX + tx, footY + 2.4);
          ctx.stroke();
        }
      }

      // Wing: big rounded hen wing swinging on the beat — the motion that
      // sells "flying", not "ball".
      ctx.save();
      ctx.translate(-1, -3);
      ctx.rotate(-0.35 + flap * 0.65);
      ctx.fillStyle = '#efe5cd';
      ctx.strokeStyle = 'rgba(140,120,88,.85)';
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.moveTo(5, -1);
      ctx.quadraticCurveTo(-1, -8.5, -8, -6);
      ctx.quadraticCurveTo(-11.5, -3.5, -9.5, 0.5);
      ctx.quadraticCurveTo(-6, 4, 3, 3);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Feather shafts across the coverts.
      ctx.strokeStyle = 'rgba(155,132,95,.7)';
      ctx.lineWidth = 0.9;
      for (const fy of [-3.4, -1.2, 1.2]) {
        ctx.beginPath();
        ctx.moveTo(-8.5, fy + 0.8);
        ctx.quadraticCurveTo(-3, fy - 1.2, 4, fy - 0.6);
        ctx.stroke();
      }
      ctx.restore();

      // Head rides the front of the bob.
      const hx = r * 0.52;
      const hy = -r * 0.62;
      const hr = 4.9;
      ctx.fillStyle = '#f8f4e9';
      ctx.strokeStyle = 'rgba(130,110,80,.55)';
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.arc(hx, hy, hr, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      // Comb: three soft red bumps on the crown.
      ctx.fillStyle = '#e05252';
      for (const [cx, cr] of [
        [-2.4, 2.1],
        [0.6, 2.6],
        [3.2, 2.1],
      ] as const) {
        ctx.beginPath();
        ctx.arc(hx + cx, hy - hr * 1.02, cr, 0, Math.PI * 2);
        ctx.fill();
      }
      // Wattle dangling under the beak.
      ctx.beginPath();
      ctx.ellipse(hx + hr - 0.4, hy + 3.4, 1.7, 2.3, 0.2, 0, Math.PI * 2);
      ctx.fill();
      // Beak with a centre seam.
      ctx.fillStyle = '#f0a53b';
      ctx.beginPath();
      ctx.moveTo(hx + hr - 0.8, hy - 1.6);
      ctx.lineTo(hx + hr + 5, hy + 1.2);
      ctx.lineTo(hx + hr - 0.8, hy + 3.4);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(160,90,20,.7)';
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.moveTo(hx + hr - 0.4, hy + 1);
      ctx.lineTo(hx + hr + 4.2, hy + 1.2);
      ctx.stroke();
      // Eye with a catchlight.
      ctx.fillStyle = '#2a2018';
      ctx.beginPath();
      ctx.arc(hx + 1.7, hy - 0.6, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,.92)';
      ctx.beginPath();
      ctx.arc(hx + 2.2, hy - 1.2, 0.55, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
      return;
    }
    if (p.weaponId === 'pigeon') {
      // City pigeon mid-flap: plump body, two real wings (far one dark and
      // opposite in phase, near one big and scalloped), fanned tail, neck
      // iridescence — a homing bird with an attitude, not a grey ellipse.
      const flap = Math.sin(nowS * 21 + p.id * 1.7); // -1..1, fast wingbeat
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang);
      // Tail: three notched feathers behind.
      ctx.fillStyle = '#83879a';
      ctx.beginPath();
      ctx.moveTo(-6, 0);
      ctx.lineTo(-13.5, -4.6);
      ctx.lineTo(-11.5, 0);
      ctx.lineTo(-13.5, 4.6);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(60,63,76,.7)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Far wing — swings behind the body, counter-phase to the near one.
      ctx.save();
      ctx.translate(0, 3);
      ctx.rotate(0.5 + flap * 0.55);
      ctx.fillStyle = '#7d818e';
      ctx.beginPath();
      ctx.moveTo(1, 0);
      ctx.quadraticCurveTo(-4, -3.5, -11, -3);
      ctx.quadraticCurveTo(-7, 1.5, -1, 3.5);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      // Body: cool grey with a lit back.
      const bg = ctx.createRadialGradient(-2.5, -3, 1.5, 0, 0, 11);
      bg.addColorStop(0, '#dfe3ea');
      bg.addColorStop(0.55, '#bcc2cf');
      bg.addColorStop(1, '#8f93a0');
      ctx.fillStyle = bg;
      ctx.beginPath();
      ctx.ellipse(0, 0, 9.5, 6, 0, 0, Math.PI * 2);
      ctx.fill();
      // Head.
      ctx.fillStyle = '#cfd4de';
      ctx.beginPath();
      ctx.arc(7, -2.2, 4.4, 0, Math.PI * 2);
      ctx.fill();
      // Neck iridescence — the pigeon's signature shimmer.
      ctx.strokeStyle = 'rgba(66,168,124,.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(4.5, -1, 3.4, -0.7, 1.2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(146,92,196,.42)';
      ctx.beginPath();
      ctx.arc(4.8, 0.4, 3.6, 0.4, 1.9);
      ctx.stroke();
      // Eye: orange ring, dark pupil, glint.
      ctx.fillStyle = '#ff9d2e';
      ctx.beginPath();
      ctx.arc(8.4, -3, 1.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#241a16';
      ctx.beginPath();
      ctx.arc(8.5, -3, 1.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,.85)';
      ctx.beginPath();
      ctx.arc(8.1, -3.5, 0.5, 0, Math.PI * 2);
      ctx.fill();
      // Beak: short orange wedge with a dark centre line.
      ctx.fillStyle = '#e07f2a';
      ctx.beginPath();
      ctx.moveTo(10.5, -2.6);
      ctx.lineTo(14.8, -1.2);
      ctx.lineTo(10.4, 0.2);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(140,70,10,.8)';
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.moveTo(10.6, -1.2);
      ctx.lineTo(14.2, -1.2);
      ctx.stroke();
      // Near wing — big sweep with scalloped feathers, flap-driven.
      ctx.save();
      ctx.translate(-0.5, -3.2);
      ctx.rotate(-1.15 + flap * 0.8);
      ctx.fillStyle = '#c9ced8';
      ctx.beginPath();
      ctx.moveTo(2, 1);
      ctx.quadraticCurveTo(-3, -4.5, -10.5, -5.5);
      ctx.quadraticCurveTo(-8.5, -2.6, -9.5, -1.8);
      ctx.quadraticCurveTo(-7, -0.6, -7.8, 0.4);
      ctx.quadraticCurveTo(-5, 1.4, -5.4, 2.6);
      ctx.quadraticCurveTo(-3, 3.4, 1.5, 3);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(97,102,118,.75)';
      ctx.lineWidth = 1.1;
      ctx.stroke();
      ctx.strokeStyle = 'rgba(120,125,142,.65)'; // feather shafts
      ctx.lineWidth = 0.9;
      for (const [x1, y1, x2, y2] of [
        [-2, -1.5, -8, -4],
        [-1.5, 0, -7, -0.8],
        [-1, 1.4, -5.4, 1.8],
      ]) {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
      ctx.restore();
      // Feet tucked under the tail.
      ctx.strokeStyle = '#d97a2a';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(-3, 4.5);
      ctx.lineTo(-6, 6.5);
      ctx.moveTo(-1.5, 4.8);
      ctx.lineTo(-4.2, 7);
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'pomegranate' && p.splitsRemaining > 0) {
      // The whole fruit: leathery red rind with seed-press blush and the
      // spiky crown. Sways upright (never tumbles) so the crown always reads.
      const rr = 13;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(Math.sin(nowS * 4.5 + p.id) * 0.14);
      const grd = ctx.createRadialGradient(-4, -4.5, 1.5, 0, 0, rr);
      grd.addColorStop(0, '#ef6a7e');
      grd.addColorStop(0.5, '#c62f4a');
      grd.addColorStop(1, '#7c192b');
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(0, 0, rr, 0, Math.PI * 2);
      ctx.fill();
      // Leathery speckle + a blush where the arils press the skin.
      ctx.fillStyle = 'rgba(110,20,40,.45)';
      for (const [dx, dy] of [
        [4.5, 3],
        [-5, 4.5],
        [6, -4],
        [-3.5, -6],
      ]) {
        ctx.beginPath();
        ctx.arc(dx, dy, 1, 0, Math.PI * 2);
        ctx.fill();
      }
      const blush = ctx.createRadialGradient(3.5, 4, 0.5, 3.5, 4, 7);
      blush.addColorStop(0, 'rgba(255,150,160,.5)');
      blush.addColorStop(1, 'rgba(255,150,160,0)');
      ctx.fillStyle = blush;
      ctx.beginPath();
      ctx.arc(3.5, 4, 7, 0, Math.PI * 2);
      ctx.fill();
      // Crown: five leathery sepals fanning off the top.
      ctx.fillStyle = '#8e1f33';
      ctx.beginPath();
      for (const [sx, tipY] of [
        [-6.2, -16.2],
        [-3.2, -17.6],
        [0, -18.2],
        [3.2, -17.6],
        [6.2, -16.2],
      ]) {
        ctx.moveTo(sx - 2.4, -10.6);
        ctx.lineTo(sx, tipY);
        ctx.lineTo(sx + 2.4, -10.6);
      }
      ctx.fill();
      // Stem button in the crown's heart.
      ctx.fillStyle = '#6b4a1f';
      ctx.beginPath();
      ctx.ellipse(0, -13.4, 2, 1.6, 0, 0, Math.PI * 2);
      ctx.fill();
      // Specular kiss + dark rim.
      ctx.fillStyle = 'rgba(255,235,235,.4)';
      ctx.beginPath();
      ctx.ellipse(-4.6, -5.4, 3.6, 2.2, -0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(90,14,30,.85)';
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      ctx.arc(0, 0, rr - 0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'pomegranate' && p.splitsRemaining === 0) {
      // Seed-bomb: a little glistening aril, not a whole fruit.
      ctx.fillStyle = '#e0455f';
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,.75)';
      ctx.beginPath();
      ctx.arc(p.x - 1.6, p.y - 1.8, 1.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(120,20,40,.6)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5.2, 0, Math.PI * 2);
      ctx.stroke();
      return;
    }
    if (p.weaponId === 'grapelet') {
      // A shattered cluster's droplet: tiny, purple, glossy.
      ctx.fillStyle = '#8a5fd0';
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,.7)';
      ctx.beginPath();
      ctx.arc(p.x - 1.5, p.y - 1.7, 1.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(60,30,110,.55)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
      ctx.stroke();
      return;
    }
    if (p.weaponId === 'grapes') {
      // The whole cluster: a bottom-heavy bunch of glossy berries under a
      // stem stub, tendril and one leaf. It hangs and sways through the arc
      // — divided only when the player taps.
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(Math.sin(nowS * 5.5 + p.id) * 0.16 - 0.08);
      const berries: [number, number, number][] = [
        // Upper row first so the lower berries overlap it — bunch depth.
        [-5.8, -3.4, 4.3],
        [0, -4.6, 4.5],
        [5.8, -3.4, 4.3],
        [-7.4, 3, 3.9],
        [7.4, 3, 3.9],
        [-3.4, 2.4, 4.7],
        [3.4, 2.4, 4.7],
        [0, 7.4, 4.6],
      ];
      for (const [bx, by, br] of berries) {
        const gg = ctx.createRadialGradient(bx - 1.6, by - 1.8, 0.6, bx, by, br);
        gg.addColorStop(0, '#a487e0');
        gg.addColorStop(0.55, '#7a4fb5');
        gg.addColorStop(1, '#4d2f7d');
        ctx.fillStyle = gg;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(45,25,80,.7)';
        ctx.lineWidth = 1.1;
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,.66)';
        ctx.beginPath();
        ctx.ellipse(bx - br * 0.34, by - br * 0.4, br * 0.3, br * 0.2, -0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      // Stem stub + curling tendril.
      ctx.strokeStyle = '#7a5a2e';
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(0, -7.5);
      ctx.quadraticCurveTo(0.6, -11, -1.4, -13.5);
      ctx.stroke();
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(-1.4, -13.5);
      ctx.quadraticCurveTo(3.5, -15.5, 4.5, -12.8);
      ctx.quadraticCurveTo(5.2, -11, 3.4, -11.2);
      ctx.stroke();
      // One vine leaf.
      ctx.fillStyle = '#4fae5c';
      ctx.beginPath();
      ctx.moveTo(-1, -12.4);
      ctx.quadraticCurveTo(-7, -17.5, -11, -14.4);
      ctx.quadraticCurveTo(-7.5, -11, -1, -12.4);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(38,104,50,.8)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-1.6, -12.5);
      ctx.lineTo(-9.4, -14.4);
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'chili') {
      // Molotov chili: pepper bottle with a live flame at the tail, throwing
      // sparks and smoke as it arcs in.
      const bx = p.x - Math.cos(ang) * 10;
      const by = p.y - Math.sin(ang) * 10;
      if (Math.random() < 0.55) {
        fx.burst('spark', bx, by, {
          color: '#ff9a2e',
          count: 1,
          speed: 26,
          jitter: 46,
          size: 2.4,
          life: 0.35,
          gravity: -50,
        });
      }
      if (Math.random() < 0.22) {
        fx.burst('smoke', bx, by, {
          color: '#6b625c',
          count: 1,
          speed: 10,
          jitter: 22,
          size: 2.6,
          life: 0.85,
        });
      }
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang);
      // Pepper body, nose forward.
      ctx.fillStyle = '#d9403a';
      ctx.beginPath();
      ctx.moveTo(10, 0);
      ctx.quadraticCurveTo(3, 5, -5, 3.6);
      ctx.lineTo(-5, -3.6);
      ctx.quadraticCurveTo(3, -5, 10, 0);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,.35)';
      ctx.beginPath();
      ctx.ellipse(3, -1.6, 3, 1.2, -0.2, 0, Math.PI * 2);
      ctx.fill();
      // Green cork/stem at the tail.
      ctx.fillStyle = '#3f9e54';
      ctx.fillRect(-7, -2.2, 3, 4.4);
      // Flickering tail flame.
      const fl = 1 + Math.sin(nowS * 31) * 0.35;
      ctx.fillStyle = 'rgba(255,150,40,.9)';
      ctx.beginPath();
      ctx.moveTo(-7, -3);
      ctx.quadraticCurveTo(-13 * fl, 0, -7, 3);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgba(255,232,120,.95)';
      ctx.beginPath();
      ctx.moveTo(-7, -1.8);
      ctx.quadraticCurveTo(-10.5 * fl, 0, -7, 1.8);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'beans') {
      // Grenade pod: a plump green pod riding its own arc — beans swelling
      // inside, seam up, a slight wobble so a 10-pod burst feels alive.
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang + Math.sin(nowS * 9 + p.id) * 0.12);
      const pod = () => {
        ctx.beginPath();
        ctx.moveTo(11, 0);
        ctx.quadraticCurveTo(7, -5.2, -1, -4.6);
        ctx.quadraticCurveTo(-8, -4, -10.5, -0.6);
        ctx.quadraticCurveTo(-8, 4.2, -1, 4.6);
        ctx.quadraticCurveTo(7, 5, 11, 0);
        ctx.closePath();
      };
      ctx.fillStyle = '#8fcf6a';
      pod();
      ctx.fill();
      // Three beans bulge the pod.
      ctx.fillStyle = '#a8dd84';
      for (const bx of [4.6, -0.6, -5.6]) {
        ctx.beginPath();
        ctx.ellipse(bx, -0.4, 3.1, 3.4, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // Seam along the spine.
      ctx.strokeStyle = 'rgba(52,104,44,.75)';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(9.5, 1.6);
      ctx.quadraticCurveTo(0, 4.4, -9.5, 1.4);
      ctx.stroke();
      // Outline + a tiny stem nub at the tail.
      ctx.strokeStyle = 'rgba(38,74,32,.8)';
      ctx.lineWidth = 1.4;
      pod();
      ctx.stroke();
      ctx.fillStyle = '#5e8f3e';
      ctx.beginPath();
      ctx.ellipse(-10.6, -0.4, 2.2, 1.4, 0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'coconut') {
      // Coconut shell cannonball: fibrous husk, three eyes, tumbling end
      // over end — field artillery with a face.
      const rr = 13;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang + p.ageS * 7);
      const grd = ctx.createRadialGradient(-4, -4, 1.5, 0, 0, rr);
      grd.addColorStop(0, '#b07640');
      grd.addColorStop(0.6, '#8a5a2b');
      grd.addColorStop(1, '#553417');
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(0, 0, rr, 0, Math.PI * 2);
      ctx.fill();
      // Husk fibers curling off the rim.
      ctx.strokeStyle = 'rgba(52,30,12,.7)';
      ctx.lineWidth = 1.4;
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + 0.4;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * (rr - 4.5), Math.sin(a) * (rr - 4.5));
        ctx.quadraticCurveTo(
          Math.cos(a + 0.16) * (rr - 1.5),
          Math.sin(a + 0.16) * (rr - 1.5),
          Math.cos(a) * rr,
          Math.sin(a) * rr,
        );
        ctx.stroke();
      }
      // The three eyes, clustered together.
      ctx.fillStyle = '#241205';
      for (const [ex, ey] of [
        [-3.2, -2.2],
        [1.4, -3.4],
        [-1, 1.8],
      ]) {
        ctx.beginPath();
        ctx.arc(ex, ey, 1.7, 0, Math.PI * 2);
        ctx.fill();
      }
      // Specular kiss + dark rim.
      ctx.fillStyle = 'rgba(255,235,200,.35)';
      ctx.beginPath();
      ctx.ellipse(-4.5, -5, 3.4, 2.1, -0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(30,16,6,.85)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, rr - 0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'watermelon') {
      // Heavy bomber: dark jagged rind stripes over a lit green ball, pale
      // speckles, dried blossom nub — a whole melon on a slow heavy tumble.
      const rr = 15.5;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang + p.ageS * 3);
      const wg = ctx.createRadialGradient(-5, -5, 2, 0, 0, rr);
      wg.addColorStop(0, '#6fcf6a');
      wg.addColorStop(0.55, '#3fa34d');
      wg.addColorStop(1, '#1c6b30');
      ctx.fillStyle = wg;
      ctx.beginPath();
      ctx.arc(0, 0, rr, 0, Math.PI * 2);
      ctx.fill();
      // Jagged rind stripes, clipped to the ball.
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, rr - 1, 0, Math.PI * 2);
      ctx.clip();
      ctx.strokeStyle = 'rgba(18,78,34,.9)';
      ctx.lineWidth = 4.2;
      ctx.lineCap = 'round';
      for (const sx of [-11, -5.5, 0, 5.5, 11]) {
        ctx.beginPath();
        ctx.moveTo(sx, -rr - 2);
        ctx.quadraticCurveTo(sx + 3, -rr * 0.35, sx - 1.5, 0);
        ctx.quadraticCurveTo(sx + 3, rr * 0.4, sx, rr + 2);
        ctx.stroke();
      }
      // Pale speckles on the rind.
      ctx.fillStyle = 'rgba(210,240,190,.5)';
      for (const [dx, dy] of [
        [-6, -7],
        [7, -4],
        [-2, 8],
        [5, 6],
      ]) {
        ctx.beginPath();
        ctx.ellipse(dx, dy, 1.4, 1, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      // Dried blossom nub + specular + dark rim.
      ctx.fillStyle = '#6b4a1f';
      ctx.beginPath();
      ctx.arc(0, rr - 2.4, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,.4)';
      ctx.beginPath();
      ctx.ellipse(-5, -6, 4.4, 2.6, -0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(12,54,24,.9)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, rr - 0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      return;
    }
    if (p.weaponId === 'lemon') {
      // The toolbar icon's lemon, flown: flat yellow oval, one pip nub on
      // the tail, the tilted green leaf on the head — rolls end over end.
      const rx = 12.6;
      const ry = 8.4;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang + p.ageS * 4.5);
      // Body — the icon's yellows, lit a touch from the top-left.
      const lg = ctx.createLinearGradient(0, -ry, 0, ry);
      lg.addColorStop(0, '#f7f07a');
      lg.addColorStop(0.5, '#e8e04a');
      lg.addColorStop(1, '#cfc532');
      ctx.fillStyle = lg;
      ctx.strokeStyle = '#b3ab1f';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      // Pip nub: the icon's pointed tip, sticking out of the tail.
      ctx.fillStyle = '#b3ab1f';
      ctx.beginPath();
      ctx.moveTo(-(rx - 3), -3.2);
      ctx.lineTo(-(rx + 3.4), 0);
      ctx.lineTo(-(rx - 3), 3.2);
      ctx.closePath();
      ctx.fill();
      // Leaf: same tilted green ellipse as the icon, laid on the head end.
      ctx.fillStyle = '#3fa34d';
      ctx.strokeStyle = 'rgba(38,104,50,.8)';
      ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.ellipse(rx - 1.5, -7.8, 4.6, 2.5, -0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath(); // midrib
      ctx.moveTo(rx - 5.3, -5.2);
      ctx.lineTo(rx + 2.3, -10.4);
      ctx.stroke();
      // Peel pores + a soft shine keep it from going flat at zoom.
      ctx.fillStyle = 'rgba(179,171,31,.55)';
      for (const [dx, dy] of [
        [-5, 2.6],
        [1, -3.6],
        [5.5, 1.6],
      ]) {
        ctx.beginPath();
        ctx.arc(dx, dy, 1, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = 'rgba(255,255,235,.5)';
      ctx.beginPath();
      ctx.ellipse(-3.6, -3.2, 4.4, 1.8, -0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }
    const r = Math.max(5, Math.min(18, def.radius * 0.3));
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill();
    if (p.weaponId === 'beehive') {
      ctx.strokeStyle = 'rgba(60,32,8,.85)'; // honeycomb bands
      ctx.lineWidth = 2;
      for (const off of [-0.4, 0.1, 0.6]) {
        ctx.beginPath();
        ctx.moveTo(p.x - r * 0.8, p.y + r * off);
        ctx.lineTo(p.x + r * 0.8, p.y + r * off);
        ctx.stroke();
      }
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.lineWidth = 2;
    ctx.stroke();
}

/**
 * A weapon resting in a character's grip — the same flight art, replayed at
 * the caller's origin along +x, for the held-gear pass. The caller has
 * already translated to the hand and rotated to the aim, so the fake
 * projectile lies at (0,0) with velocity +1,0 (angle 0). Idle life keeps
 * running off `nowS` — sway, wing flap, flame flicker — while age-based
 * tumbles freeze at zero and the particle sink is a no-op: a held chili
 * keeps its flame but never sparks at whoever is holding it. This is how
 * every barrelless weapon (and any added later) gets in-hand art: recognized
 * weapons draw their own shapes, unknown ids fall through to the ball.
 */
export function drawHeldWeaponArt(
  ctx: CanvasRenderingContext2D,
  weaponId: WeaponId,
  nowS: number,
): void {
  drawProjectile(
    ctx,
    {
      id: 0,
      weaponId,
      ownerId: -1,
      teamId: -1,
      x: 0,
      y: 0,
      vx: 1, // atan2(0, 1) = 0 — art lies flat along the aim line
      vy: 0,
      ageS: 0, // no in-hand tumble: the aim rotation is the only motion
      bounces: 0,
      fuseS: -1,
      armS: -1,
      stuck: false,
      splitsRemaining: 1, // the whole pomegranate, not a split seed-bomb
      fromPellet: false,
      trailAcc: 0,
      behavior: 'none',
      timerS: -1,
      lockTargetId: null,
    },
    { nowS, fx: { burst: () => {} } },
  );
}
